import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { KEYBOARD_KEYS } from '../../constants/keyboardKeys';
import { getEnrichedTeamsData, teamMemberHref, teamTabFromParam, teamTabToParam, teams } from '../../models/content';

const LEADERSHIP_ORDER = ['rajesh-sharma', 'sanjay-jain', 'pr-srinivasan', 'devinjit-singh'];

function tabId(key) {
  return `team-tab-${teamTabToParam(key)}`;
}

function panelId(key) {
  return `team-panel-${teamTabToParam(key)}`;
}

function sortMembers(members, activeKey) {
  const deduped = [];
  const seen = new Set();
  members.forEach((m) => {
    if (seen.has(m.slug)) return;
    seen.add(m.slug);
    deduped.push(m);
  });

  if (activeKey !== 'Leadership Teams') return deduped;

  return [...deduped].sort((a, b) => {
    const ai = LEADERSHIP_ORDER.indexOf(a.slug);
    const bi = LEADERSHIP_ORDER.indexOf(b.slug);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function TeamCard({ member, activeKey }) {
  return (
    <Link
      className="team-card-link"
      to={teamMemberHref(member.slug, activeKey)}
      aria-label={`View profile of ${member.name}`}
    >
      <article className="team-card">
        <div
          className="portrait"
          style={{
            backgroundImage: member.image ? `url("${member.image}")` : undefined,
            backgroundColor: member.image ? undefined : 'var(--p100)',
          }}
        />
        <div className="meta">
          <h3 className="name">{member.name}</h3>
          <div className="role">{member.role}</div>
          {member.bio ? <p className="bio">{member.bio}</p> : null}
        </div>
      </article>
    </Link>
  );
}

export default function V2TeamsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = teamTabFromParam(searchParams.get('tab'));
  const [focusKey, setFocusKey] = useState(activeKey);
  const enrichedData = useMemo(() => getEnrichedTeamsData(), []);
  const tabKeys = teams.tabs.map((t) => t.key);

  useEffect(() => {
    setFocusKey(activeKey);
  }, [activeKey]);

  const setActiveKey = (key) => {
    setFocusKey(key);
    setSearchParams({ tab: teamTabToParam(key) }, { replace: true });
  };

  const membersByTab = useMemo(() => {
    const out = {};
    teams.tabs.forEach((t) => {
      const list = enrichedData[t.key] ?? [];
      out[t.key] = sortMembers(Array.isArray(list) ? list : [], t.key);
    });
    return out;
  }, [enrichedData]);

  const focusTab = (key) => {
    setFocusKey(key);
    requestAnimationFrame(() => document.getElementById(tabId(key))?.focus());
  };

  const onTabKeyDown = (e) => {
    const i = tabKeys.indexOf(focusKey);
    if (i < 0) return;

    const handlers = {
      [KEYBOARD_KEYS.ARROW_RIGHT]: () => focusTab(tabKeys[(i + 1) % tabKeys.length]),
      [KEYBOARD_KEYS.ARROW_DOWN]: () => focusTab(tabKeys[(i + 1) % tabKeys.length]),
      [KEYBOARD_KEYS.ARROW_LEFT]: () => focusTab(tabKeys[(i - 1 + tabKeys.length) % tabKeys.length]),
      [KEYBOARD_KEYS.ARROW_UP]: () => focusTab(tabKeys[(i - 1 + tabKeys.length) % tabKeys.length]),
      [KEYBOARD_KEYS.HOME]: () => focusTab(tabKeys[0]),
      [KEYBOARD_KEYS.END]: () => focusTab(tabKeys[tabKeys.length - 1]),
      [KEYBOARD_KEYS.ENTER]: () => setActiveKey(focusKey),
      [KEYBOARD_KEYS.SPACE]: () => setActiveKey(focusKey),
    };

    const handler = handlers[e.key];
    if (handler) {
      e.preventDefault();
      handler();
    }
  };

  return (
    <main>
      <section className="page-hero">
        <div className="wrap">
          <span className="v2-badge">
            <span className="dot" />
            {teams.heroBadge}
          </span>
          <h1>
            {teams.heroTitle.map((p, i) =>
              typeof p === 'string' ? (
                <React.Fragment key={i}>{p}</React.Fragment>
              ) : (
                <span key={i} className="it">
                  {p.it}
                </span>
              )
            )}
          </h1>
        </div>
      </section>

      <section>
        <div className="wrap">
          <p className="teams-intro">
            {teams.intro.split('100 years').map((chunk, idx, arr) => (
              <React.Fragment key={idx}>
                {chunk}
                {idx < arr.length - 1 ? <strong>100 years</strong> : null}
              </React.Fragment>
            ))}
          </p>

          <div className="tabs-row">
            <div
              className="tabs"
              role="tablist"
              aria-label="Teams tabs"
              onKeyDown={onTabKeyDown}
            >
              {teams.tabs.map((t) => {
                const selected = activeKey === t.key;
                return (
                  <button
                    key={t.key}
                    id={tabId(t.key)}
                    type="button"
                    className={`tab ${selected ? 'is-active' : ''}`}
                    role="tab"
                    aria-selected={selected}
                    aria-controls={panelId(t.key)}
                    tabIndex={focusKey === t.key ? 0 : -1}
                    onClick={() => setActiveKey(t.key)}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {teams.tabs.map((t) => {
            const selected = activeKey === t.key;
            return (
              <div
                key={t.key}
                id={panelId(t.key)}
                className={`team-grid ${selected ? 'team-grid-animated' : ''}`}
                role="tabpanel"
                aria-labelledby={tabId(t.key)}
                hidden={!selected}
              >
                {(membersByTab[t.key] ?? []).map((m) => (
                  <TeamCard key={m.slug} member={m} activeKey={t.key} />
                ))}
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
